<script setup>
  import { computed, ref, watch, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import useSortableList from '../composables/useSortableList.js';
  import CohortPageForm from './CohortPageForm.vue';
  import CohortPageCarousel from './CohortPageCarousel.vue';

  const { t } = useI18n();

  const props = defineProps({
    cohorts: {
      type: Array,
      required: true
    },
    current_cohort_id: {
      type: Number,
      required: false
    },
    languages: {
      type: Array,
      required: true
     }
  });

  const selectedCohortId = ref(props.current_cohort_id);
  const pages = ref([]);
  const currentEditingPageId = ref(null);
  const pagesList = ref(null);

  const loadPages = async () => {
    currentEditingPageId.value = null; // Reset the current editing page when loading pages
    try {
      const res = await fetch(`/admin/cohort_pages?cohort_id=${selectedCohortId.value}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
      pages.value = (await res.json()).pages
    } catch (error) {
      console.error('Error fetching pages:', error);
      pages.value = [];
    }
  };

  watch(selectedCohortId, async (newVal) => {
    await loadPages();
  });

  useSortableList(pagesList, pages, '.page-drag-handle', (page, position) => move(page.id, position));

  onMounted(async () => {
    if (selectedCohortId.value) {
      await loadPages();
    }
  });

  const move = async (pageId, position) => {
    try {
      const res = await fetch(`/admin/cohort_pages/${pageId}/move`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ position })
      });
      if (!res.ok) throw new Error(res.statusText);
    } catch (error) {
      console.error('Error moving page:', error);
      await loadPages();
    }
  };

  const newPage = () => {
    currentEditingPageId.value = 0;
  };

  const editPage = (pageId) => {
    currentEditingPageId.value = pageId;
  };

  const deletePage = async (pageId) => {
    if (confirm(t('cohort_pages.delete_confirm'))) {
      try {
        await fetch(`/admin/cohort_pages/${pageId}.json`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        });
        await loadPages();
      } catch (error) {
        console.error('Error deleting page:', error);
      }
    }
  };
</script>

<template>
  <div class="row mb-3">
    <div class="col" v-if="props.cohorts.length > 0">
      <label for="cohort_id" class="form-label">{{ t('cohort_pages.cohort') }}</label>
      <select v-if="props.cohorts.length > 1" class="form-select" id="cohort_id" name="cohort_id" v-model="selectedCohortId">
        <option v-for="cohort in props.cohorts" :key="cohort.id" :value="cohort.id">{{ cohort.name }}</option>
      </select>
      <input v-else class="form-control" type="text" :value="props.cohorts[0].name" disabled readonly>
    </div>
    <div class="col" v-else>
      <div class="alert alert-primary" role="alert">
        {{ t('cohort_pages.no_cohorts') }}
      </div>
    </div>
  </div>

  <div class="row">
    <div class="col">
      <label for="pages" class="form-label">{{ t('cohort_pages.pages') }}</label>

      <div class="alert alert-primary" role="alert" v-if="pages.length === 0 && currentEditingPageId == null">
        {{ t('cohort_pages.no_pages') }}
      </div>

      <div class="row mb-2" v-if="currentEditingPageId != 0">
        <div class="col">
          <button class="btn btn-sm btn-primary ms-0" @click="newPage">{{ t('cohort_pages.new_page') }}</button>
        </div>
      </div>
      <div  v-if="currentEditingPageId == 0" class="border rounded mb-2">
        <CohortPageForm v-model="currentEditingPageId" @saved="loadPages" :languages="props.languages" :cohort_id="selectedCohortId" />
      </div>

      <div ref="pagesList">
      <div v-for="page in pages" :key="page.id" class="border rounded mb-2">
        <template v-if="currentEditingPageId !== page.id">
          <div class="row my-2">
            <div class="col-6 d-flex align-items-center ms-2">
              <span class="drag-handle page-drag-handle me-2" data-bs-toggle="tooltip" :data-bs-title="t('cohort_pages.drag_to_reorder')" :aria-label="t('cohort_pages.drag_to_reorder')"><i class="bi bi-grip-vertical" aria-hidden="true"></i></span>
              {{ page.title }}
            </div>
            <div class="col d-flex align-items-center">
              <span class="me-1" v-if="page.display_in_menu" data-bs-toggle="tooltip" :data-bs-title="t('cohort_pages.display_in_menu')"><i class="bi bi-menu-button" aria-hidden="true"></i></span>
              <span class="me-1" v-if="page.shortcut" data-bs-toggle="tooltip" :data-bs-title="t('cohort_pages.shortcut')"><i class="bi bi-box-arrow-up-right" aria-hidden="true"></i></span>
              <span class="me-1" v-if="page.highlight" data-bs-toggle="tooltip" :data-bs-title="t('cohort_pages.highlight')"><i class="bi bi-highlighter" aria-hidden="true"></i></span>
              <span class="me-1" v-if="page.display_saved_searches" data-bs-toggle="tooltip" :data-bs-title="t('cohort_pages.display_saved_searches')"><i class="bi bi-search-heart" aria-hidden="true"></i></span>
              <span class="me-1" v-if="page.home_page" data-bs-toggle="tooltip" :data-bs-title="t('cohort_pages.home_page')"><i class="bi bi-house" aria-hidden="true"></i></span>
              <span class="me-1" v-if="page.direct_link" data-bs-toggle="tooltip" :data-bs-title="t('cohort_pages.direct_link')"><i class="bi bi-link" aria-hidden="true"></i></span>
            </div>
            <div class="col-auto d-flex align-items-center me-2">
              <div class="btn-toolbar" role="toolbar">
                <div class="btn-group form-btn-group" role="group">
                  <button class="btn btn-sm btn-primary" data-bs-toggle="tooltip" :data-bs-title="t('cohort_pages.edit')" :aria-label="t('cohort_pages.edit')" @click="editPage(page.id)"><i class="bi bi-pencil-fill" aria-hidden="true"></i></button>
                  <button class="btn btn-sm btn-danger" data-bs-toggle="tooltip" :data-bs-title="t('cohort_pages.delete')" :aria-label="t('cohort_pages.delete')" @click="deletePage(page.id)"><i class="bi bi-trash3-fill" aria-hidden="true"></i></button>
                </div>
                <div class="btn-group form-btn-group ms-2" role="group" v-if="!page.direct_link">
                  <button :class="`btn btn-sm btn-primary ${page.expanded ? 'active' : ''}`" @click="page.expanded = !page.expanded" :aria-pressed="page.expanded">{{ t('cohort_pages.carousels') }} <i :class="`bi bi-chevron-${page.expanded ? 'up' : 'down'}`" aria-hidden="true"></i></button>
                </div>
              </div>
            </div>
          </div>
          <div class="row my-2" v-if="page.expanded">
            <div class="col mx-4 my-1 border rounded text-bg-light">
              <CohortPageCarousel :cohort_page_id="page.id" />
            </div>
          </div>
        </template>
        <CohortPageForm v-else v-model="currentEditingPageId" @saved="loadPages" :languages="props.languages" :cohort_id="selectedCohortId"/>
      </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .drag-handle {
    cursor: grab;
  }
</style>

<i18n src="../locales/en.json" global></i18n>
<i18n src="../locales/fr.json" global></i18n>
<i18n src="../locales/es.json" global></i18n>
<i18n src="../locales/it.json" global></i18n>
<i18n src="../locales/pt.json" global></i18n>
<i18n src="../locales/de.json" global></i18n>
